import { Injectable, inject } from "@angular/core";
import { HttpLoggerService } from "./HttpLoggerService.service";
import { firstValueFrom } from "rxjs";
import { selectEmpleadorUser } from "app/store/selectors/empleador.selectors";
import { Store } from "@ngrx/store";
import * as CryptoJS from "crypto-js";
import { ConfigService } from "@core/config/app.config.service";

@Injectable({ providedIn: "root" })
export class LoggerService {
  private readonly configService = inject(ConfigService);
  private readonly httpLogger = inject(HttpLoggerService);
  private readonly _store = inject(Store);

  async log(
    context: string,
    data: any,
    ejecucionId: string = "Ejecucion-Info",
    idSupportUser,
    profiles,
    no_id,
    razon_social,
    transactionType,
    id_afiliation,
    id_novedad,
    id_employer,
  ): Promise<void> {
    try {
      const empleador = await firstValueFrom(
        this._store.select(selectEmpleadorUser),
      );
      const id_empleador =
        empleador?.id_empleador ?? sessionStorage.getItem("id_empleador");
      const id_aportante =
        empleador?.id_aportante ?? sessionStorage.getItem("id_aportante");

      const idEntidadStorage = sessionStorage.getItem("id_entidad");

      const id_entidad = idEntidadStorage ? Number(idEntidadStorage) : null;
      const nombre_entidad = sessionStorage.getItem("nombre_entidad") ?? null;

      const paramsOriginal = {
        id_empleador,
        id_aportante,
        ejecucion_id: ejecucionId,
        servicio: context,
        mensaje: data,
        idSupportUser,
        profiles,
        no_id,
        razon_social,
        transactionType,
        id_afiliation,
        id_novedad,
        id_employer,
        id_entidad,
        nombre_entidad,
      };

      const paramsEncrypted = this.buildEncryptedLogPayload(paramsOriginal);

      if (this.shouldSendLog(context) && !this.hasExcludedKeywords(context)) {
        this.httpLogger.sendLog(paramsEncrypted).subscribe({
          next: () => {},
          error: (err) => console.error("[LOG ERROR]", err),
        });
      }
    } catch (err) {
      console.error("Error interno en LoggerService", err);
    }
  }

  async logInfo(
    context: string,
    ejecucionId: string,
    data: any,
    idSupportUser,
    profiles,
    no_id,
    razon_social,
    transactionType,
    id_afiliation,
    id_novedad,
    id_employer,
  ): Promise<void> {
    await this.log(
      context,
      data,
      ejecucionId,
      idSupportUser,
      profiles,
      no_id,
      razon_social,
      transactionType,
      id_afiliation,
      id_novedad,
      id_employer,
    );
  }
  async logError(
    context: string,
    data: any,
    idSupportUser,
    profiles,
    no_id,
    razon_social,
    transactionType,
    id_afiliation,
    id_novedad,
    id_employer,
  ): Promise<void> {
    await this.log(
      context,
      data,
      "Ejecucion-Error",
      idSupportUser,
      profiles,
      no_id,
      razon_social,
      transactionType,
      id_afiliation,
      id_novedad,
      id_employer,
    );
  }

  private shouldSendLog(context: string): boolean {
    if (!context) return false;
    const url = context.toLowerCase();

    if (url.includes("/api/afiliacion/registros/logs")) return false;
    if (this.hasFileExtension(url)) return false;
    return true;
  }

  private hasFileExtension(url: string): boolean {
    const excludedExt = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".svg",
      ".ico",
      ".json",
    ];
    const cleanUrl = url.split(/[?#]/)[0].toLowerCase();
    return excludedExt.some((ext) => cleanUrl.endsWith(ext));
  }
  private hasExcludedKeywords(context: string): boolean {
    if (!context) return false;
    const excludedKeywords = [
      "parametricas",
      "parametros",
      "notificaciones",
      "parametro",
    ];
    const normalized = context.toLowerCase();
    return excludedKeywords.some((keyword) => normalized.includes(keyword));
  }

  private buildEncryptedLogPayload(params: any): {
    payload: string;
    encrypted: boolean;
  } {
    return {
      payload: this.encryptLogPayload(params),
      encrypted: true,
    };
  }
  private encryptLogPayload(params: any): string {
    const logEncryptionKey = this.getCryptoKey();

    if (!logEncryptionKey) {
      throw new Error("No existe CRYPTO_KEY configurada para cifrar logs.");
    }

    return CryptoJS.AES.encrypt(
      JSON.stringify(params),
      logEncryptionKey,
    ).toString();
  }
  private getCryptoKey(): string {
    const cryptoKey = this.configService.config?.CRYPTO_KEY?.trim() ?? "";
    return cryptoKey;
  }
}
